import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy } from 'lucide-react';
import { gamesApi } from '../../api/games';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

import getGameType from '../../utils/games';
import getGameImage from '../../utils/getGameImage';
import EditGameSettings from '../../components/Popup/EditGameSettings';

// Format time from 24-hour format (HH:MM) to 12-hour format with AM/PM (h:MMAM/PM)
const formatTime = (timeString) => {
  if (!timeString) return '';

  // Split the time string to get hours and minutes
  const [hours, minutes] = timeString
    .split(':')
    .map((part) => parseInt(part, 10));

  // Convert to 12-hour format
  const period = hours >= 12 ? ' PM' : ' AM';
  const formattedHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM

  // Format the time string
  return `${formattedHours}:${minutes.toString().padStart(2, '0')}${period}`;
};

export default function GameSettingsCard({
  id,
  name,
  otherSettings,
  onUpdate,
}) {
  const { tokens } = useAuth();
  const [duplicating, setDuplicating] = useState(false);
  const gameType = getGameType(otherSettings?.image);
  const GameImage = getGameImage(gameType);
  const drawTimes = otherSettings?.drawTime || [];
  const cutOffTimes = otherSettings?.cutOffTime || [];
  const [editPopupOpen, setEditPopupOpen] = useState(false);

  const handleDuplicate = async () => {
    if (duplicating) return;

    try {
      setDuplicating(true);
      // Create a new game with the same settings but with "(Copy)" appended to the name
      const newGame = {
        name: `${name} (Copy)`,
        otherSettings: { ...otherSettings },
      };

      const response = await gamesApi.addGameSettings(
        tokens.access.token,
        newGame
      );

      if (response.status === 201 || response.status === 200) {
        toast.success('Game duplicated successfully');
        if (onUpdate) onUpdate(); // Refresh the games list
      } else {
        toast.error('Failed to duplicate game');
      }
    } catch (error) {
      console.error('Error duplicating game:', error);
      toast.error('Error duplicating game');
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <div className='py-4'>
      <div className='flex gap-3'>
        <div className='w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center'>
          <img src={GameImage} className='w-full h-full object-cover' />
        </div>

        <div className='flex-1'>
          <div className='flex justify-between items-start mb-2'>
            <h3 className='text-sm font-semibold text-[#12131A]'>{name}</h3>
            <div className='flex items-center gap-3'>
              <button
                onClick={handleDuplicate}
                disabled={duplicating}
                className='text-[#65758B] hover:text-[#0E64D1] text-xs font-medium flex items-center gap-1 transition-colors'
              >
                <Copy size={14} />
                {duplicating ? 'Duplicating...' : 'Duplicate'}
              </button>
              <Link
                to='#'
                onClick={(e) => {
                  e.preventDefault();
                  setEditPopupOpen(true);
                }}
                className='text-[#0E64D1] text-xs font-medium hover:underline'
              >
                EDIT
              </Link>
            </div>
          </div>

          <div className='space-y-2.5'>
            <div>
              <div className='text-xs text-[#65758B]'>Draw Time</div>
              <div className='flex flex-wrap gap-2 mt-1'>
                {drawTimes.length > 0 ? (
                  drawTimes.map((time, index) => (
                    <span
                      key={index}
                      className='text-xs font-medium bg-gray-100 px-2 py-1 rounded-md'
                    >
                      {formatTime(time)}
                    </span>
                  ))
                ) : (
                  <span className='text-xs text-gray-500'>
                    No draw times set
                  </span>
                )}
              </div>
            </div>

            <div>
              <div className='text-xs text-[#65758B]'>Cut off Time</div>
              <div className='flex flex-wrap gap-2 mt-1'>
                {cutOffTimes.length > 0 ? (
                  cutOffTimes.map((time, index) => (
                    <span
                      key={index}
                      className='text-xs font-medium bg-gray-100 px-2 py-1 rounded-md'
                    >
                      {formatTime(time)}
                    </span>
                  ))
                ) : (
                  <span className='text-xs text-gray-500'>
                    No cut off times set
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <EditGameSettings
        isOpen={editPopupOpen}
        onClose={() => {
          setEditPopupOpen(false);
          if (onUpdate) onUpdate();
        }}
        gameData={{ id, name, otherSettings }}
      />
    </div>
  );
}
